// ── NHC-style current-position symbols for active systems (map + list) ──
// Hurricanes: the two-armed cyclone symbol with a filled centre (arms curl
// the Northern-Hemisphere way, like an "S"); tropical storms: the same with
// an open centre; depressions / subtropical / post-tropical: a lettered disc
// (D / S / L, and P for a Potential Tropical Cyclone — advisories before it
// forms); invests: NHC's ✕. Filled with the Saffir-Simpson colour; a
// dark keyline keeps them readable over satellite imagery.

import type { SystemKind } from "../engine/liveSystems";

const KEY = "#0b1220";
const ARMS = '<path d="M24 29C19 14 33 3 48 9"/><path d="M40 35C45 50 31 61 16 55"/>';

function svg(kind: SystemKind, color: string): string {
  let body: string;
  if (kind === "major" || kind === "hurricane" || kind === "storm") {
    const centre = kind === "storm"
      ? `<circle cx="32" cy="32" r="9" fill="${KEY}" stroke="${color}" stroke-width="5"/>`
      : `<circle cx="32" cy="32" r="11" fill="${color}" stroke="${KEY}" stroke-width="3"/>`;
    body =
      `<g fill="none" stroke-linecap="round"><g stroke="${KEY}" stroke-width="11">${ARMS}</g>` +
      `<g stroke="${color}" stroke-width="6.5">${ARMS}</g></g>${centre}` +
      (kind === "major" ? `<circle cx="32" cy="32" r="3.5" fill="${KEY}"/>` : "");
  } else if (kind === "invest") {
    const x = '<path d="M19 19L45 45M45 19L19 45"/>';
    body = `<g fill="none" stroke-linecap="round"><g stroke="${KEY}" stroke-width="13">${x}</g>` +
      `<g stroke="${color}" stroke-width="7.5">${x}</g></g>`;
  } else {
    const letter = kind === "depression" ? "D" : kind === "subtropical" ? "S" : kind === "potential" ? "P" : "L";
    body = `<circle cx="32" cy="32" r="16" fill="${color}" stroke="${KEY}" stroke-width="3.5"/>` +
      `<text x="32" y="33" text-anchor="middle" dominant-baseline="central" font-family="Inter,Arial,sans-serif" ` +
      `font-weight="800" font-size="20" fill="${KEY}">${letter}</text>`;
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">${body}</svg>`;
}

const urls = new Map<string, string>();

/** Data URL of a 64×64 symbol (cached per kind + colour). */
export function systemIconUrl(kind: SystemKind, color: string): string {
  const key = `${kind}|${color}`;
  let url = urls.get(key);
  if (!url) {
    url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg(kind, color))}`;
    urls.set(key, url);
  }
  return url;
}

/** On-map size (px): majors biggest, invests smallest. */
export function systemIconSize(kind: SystemKind): number {
  return kind === "major" ? 42 : kind === "hurricane" ? 38 : kind === "storm" || kind === "potential" ? 34
    : kind === "invest" ? 26 : 30;
}
